import React, { useMemo } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { useTableData } from 'src/serverProxy/useTableData';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useAppSelector, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { REVIEW_SENTIMENT_ENUM } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/constants';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { safeDivide } from 'src/utils/app';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

interface ReviewSentimentResponse {
  reviewSentiment_value_count_value: number;
  reviewSentiment_value_count_count: number;
  reviewSentiment_value_count_value_202301_202326: number;
  reviewSentiment_value_count_count_202301_202326: number;
  metadata: {
    reviewSentiment: '1' | '0' | '-1';
    name: '1' | '0' | '-1';
  };
}

const getTotalSentimentVolume = (data: ReviewSentimentResponse[]) => {
  try {
    const positiveSentimentVolume =
      data.find((datum) => Number(datum?.metadata?.name) === REVIEW_SENTIMENT_ENUM.POSITIVE)
        ?.reviewSentiment_value_count_value || 0;
    const neutralSentimentVolume =
      data.find((datum) => Number(datum?.metadata?.name) === REVIEW_SENTIMENT_ENUM.NEUTRAL)
        ?.reviewSentiment_value_count_value || 0;
    const negativeSentimentVolume =
      data.find((datum) => Number(datum?.metadata?.name) === REVIEW_SENTIMENT_ENUM.NEGATIVE)
        ?.reviewSentiment_value_count_value || 0;

    const totalVolume = positiveSentimentVolume + neutralSentimentVolume + negativeSentimentVolume;
    return totalVolume;
  } catch (err) {
    console.error('Failed to get total sentiment volume', err);
    return 0;
  }
};

const COLUMN_CHART_WIDTH = 630;
/**
 * A donut chart and topEntity chart that can be found on review tab.
 */
// Combined two graph (donut and topEntity chart) since their requests are identical
const SentimentDonutAndColumnCharts = () => {
  const theme = useStacklineTheme();
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const { isLoading, data } = useTableData({
    fields: [{ name: 'reviewSentiment' }],
    groupByFieldName: 'reviewSentiment',
    indexName: BEACON_INDEX_NAMES.REVIEWS_METRICS,
    itemsPerPage: 10000,
    pageNumber: 1,
    requestId: 'review-sentiment-donutChart-and-columnChart'
  });

  // Calculate the review sentiment score for the donut
  const reviewSentimentScore = useMemo(() => {
    if (data && data?.length) {
      try {
        const positiveSentimentVolume =
          data.find((datum) => Number(datum?.metadata?.name) === REVIEW_SENTIMENT_ENUM.POSITIVE)
            ?.reviewSentiment_value_count_value || 0;
        const totalVolume = getTotalSentimentVolume(data as ReviewSentimentResponse[]);
        return safeDivide(positiveSentimentVolume, totalVolume, 0);
      } catch (err) {
        console.error('Failed to calculate review sentiment score', err);
        return 0;
      }
    } else {
      return 0;
    }
  }, [data]);

  // Calculate the primary values for the column chart
  const currentValues = useMemo(() => {
    try {
      const currValues = (data as ReviewSentimentResponse[])?.reduce((acc, datum) => {
        const sentimentType = Number(datum?.metadata?.name);

        const volume = datum?.reviewSentiment_value_count_value || 0;
        const totalVolume = getTotalSentimentVolume(data as ReviewSentimentResponse[]);
        const value = safeDivide(volume, totalVolume, 0);

        acc[sentimentType] = value;
        return acc;
      }, {});

      return [
        { name: 'Positive', id: REVIEW_SENTIMENT_ENUM.POSITIVE },
        { name: 'Neutral', id: REVIEW_SENTIMENT_ENUM.NEUTRAL },
        { name: 'Negative', id: REVIEW_SENTIMENT_ENUM.NEGATIVE }
      ].map(({ name, id }) => [name, currValues[id] || 0]);
    } catch (err) {
      console.error('Failed to get current sentiment values', err);
      return [];
    }
  }, [data]);

  return (
    <Flex flexDirection="column" gap="xxl">
      <Flex alignItems="center" gap={108}>
        <BeaconChartWithLegend
          title="Review Sentiment"
          includeSummaryInfo={false}
          subtitle={
            <BeaconSubtitleContainer>
              <Text variant="body1">
                {`The chart displays the review sentiment for ${entityPhrase} `}
                {taxonomyText} {`during the ${mainTimePeriodShortDisplayName} period`}
                .
                <SubtitleFilters />
              </Text>
            </BeaconSubtitleContainer>
          }
          showExportMenu={false}
        >
          <Flex marginTop="8px">
            <div style={{ width: '308px', height: '308px' }}>
              {isLoading ? <DonutChartLoading /> : <CircularProgressPercentage percentage={reviewSentimentScore} />}
            </div>
            <div style={{ marginLeft: '92px' }}>
              <BeaconChartWithLegend
                containerStyles={{ width: 640 }}
                showTitleSummary={false}
                primaryLegendProps={{
                  displayName: legendLabel
                }}
                convertSeriesToDelimitedData={async () =>
                  saveSingleBarChartAsDelimitedData(currentValues as [string, number][], {
                    fileTitle: 'Review Sentiment',
                    metricLabel: 'Review Sentiment'
                  })
                }
                loading={isLoading}
                loadingComponent={<SplineChartLoading width={543} />}
              >
                <TopEntityChartV2
                  containerSx={{ width: COLUMN_CHART_WIDTH }}
                  seriesColors={[theme.colors.primary]}
                  chartSeries={[
                    {
                      type: 'column',
                      data: currentValues,
                      metricType: METRICTYPE.PERCENT
                    }
                  ]}
                  chartOptionsOverride={{
                    chart: {
                      height: 240,
                      width: COLUMN_CHART_WIDTH,
                      marginBottom: 39,
                      spacingBottom: 0,
                      marginLeft: -45
                    }
                  }}
                  leftAlignBars={false}
                />
              </BeaconChartWithLegend>
            </div>
          </Flex>
        </BeaconChartWithLegend>
      </Flex>
    </Flex>
  );
};

export default SentimentDonutAndColumnCharts;
